import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import SEO from '@/components/SEO';
import ProfileCard from '@/components/ProfileCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ========== AI CHATBOT ========== */
const AIChatBot = ({ onClose }) => {
  const [messages, setMessages] = useState([{ role: 'assistant', text: 'Hi! I can help you discover incredible women leaders. Try:\n\n"Who are the top women in tech?"\n"Tell me about women in finance"\n"Find leaders from Africa"' }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/discover`, { query: userMsg });
      setMessages((prev) => [...prev, { role: 'assistant', text: res.data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.' }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[70] w-[400px] max-w-[calc(100vw-48px)] bg-white border border-[#1A1A1A]/10 shadow-2xl flex flex-col" style={{ height: '520px' }} data-testid="ai-chatbot">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1A1A]/10 bg-[#1A1A1A]">
        <div>
          <p className="text-[13px] text-white font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>AI Profile Discovery</p>
          <p className="text-[10px] text-white/50" style={{ fontFamily: "'DM Mono', monospace" }}>Powered by GPT</p>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? 'bg-[#C25E3E] text-white' : 'bg-[#F3EFE9] text-[#1A1A1A]'}`} style={{ fontFamily: msg.role === 'assistant' ? "'DM Mono', monospace" : "'Outfit', sans-serif" }}>
              {msg.text}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-[#F3EFE9] px-4 py-3 text-[13px] animate-pulse" style={{ fontFamily: "'DM Mono', monospace" }}>Searching profiles...</div></div>}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-[#1A1A1A]/10 p-3 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage()} placeholder="Ask about women leaders..." className="flex-1 px-4 py-2.5 text-[13px] border border-[#1A1A1A]/10 bg-[#FAF9F6] outline-none focus:border-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="ai-chat-input" />
        <button onClick={sendMessage} className="px-4 bg-[#C25E3E] text-white hover:bg-[#A84D32] transition-colors" data-testid="ai-chat-send"><Send size={16} /></button>
      </div>
    </div>
  );
};

/* ========== ANIMATED COUNTER ========== */
const AnimatedCounter = ({ target, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting && !started) setStarted(true); }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = Math.max(1, Math.floor(target / 60));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [started, target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

/* ========== HERO SLIDES — Real leaders with real quotes ========== */
const HERO_SLIDES = [
  { name: 'Huda Kattan', slug: 'huda-kattan', quote: 'I started with a blog and $6,000. They said the beauty market was saturated. We built a billion-dollar brand anyway.', role: 'FOUNDER & CEO, HUDA BEAUTY', tagline: 'Celebrating women who built something extraordinary.' },
  { name: 'Randa Sadik', slug: 'randa-sadik', quote: 'Leading a bank founded in 1930 across 30 countries taught me that transformation is not about disruption — it is about precision.', role: 'CEO, ARAB BANK', tagline: 'Real stories of women who changed everything.' },
  { name: 'Falguni Nayar', slug: 'falguni-nayar', quote: 'I left a two-decade banking career at 50 with $2 million of my own savings. They said I was too late. The IPO was oversubscribed 82 times.', role: 'FOUNDER & CEO, NYKAA', tagline: 'A book. A film. A movement.' },
  { name: 'Sima Ganwani Ved', slug: 'sima-ganwani-ved', quote: 'From a single store in Dubai to 85 brands across 14 countries. Eva Longoria called me the face of female empowerment.', role: 'FOUNDER & CHAIRWOMAN, APPAREL GROUP', tagline: 'The evidence the world never had.' },
  { name: 'Lisa Su', slug: 'lisa-su', quote: 'When I took over, they said AMD was finished. We proved that the best technology wins — every single time.', role: 'CEO & CHAIR, AMD', tagline: 'Documenting women who built empires.' },
  { name: 'Mary Barra', slug: 'mary-barra', quote: 'Leading the oldest car company in America through the biggest transformation in automotive history required one thing: courage.', role: 'CEO & CHAIR, GENERAL MOTORS', tagline: 'Every story authenticated. Every fact sourced.' },
];

/* ========== DYNAMIC HERO ========== */
const DynamicHero = ({ profiles, stats }) => {
  const [slideIdx, setSlideIdx] = useState(0);
  const [entered, setEntered] = useState(false);

  useEffect(() => { setTimeout(() => setEntered(true), 100); }, []);
  useEffect(() => {
    const t = setInterval(() => setSlideIdx((p) => (p + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const currentSlide = HERO_SLIDES[slideIdx];

  // Match each slide to its exact profile by slug
  const getProfileForSlide = (slide) => {
    return profiles.find(p => p.slug === slide.slug) || null;
  };

  const currentProfile = getProfileForSlide(currentSlide);

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#FAF9F6]" data-testid="hero-section">
      {/* Background — large featured image matched to current quote */}
      <div className="absolute top-0 right-0 w-[55%] h-full hidden lg:block overflow-hidden">
        {HERO_SLIDES.map((slide, i) => {
          const prof = getProfileForSlide(slide);
          const isActive = i === slideIdx;
          if (!prof || !prof.image_url) return null;
          return (
            <div key={slide.slug} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 2 : 1 }}>
              <img src={prof.image_url} alt={slide.name} className="w-full h-full object-cover object-top" style={{ filter: 'brightness(0.95)', transform: isActive ? 'scale(1)' : 'scale(1.05)', transition: 'transform 6s ease-out' }} />
              <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6] via-[#FAF9F6]/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F6] via-transparent to-[#FAF9F6]/30" />
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-12 pt-44 pb-20 lg:pb-28 min-h-screen flex flex-col justify-end">
        <div className="max-w-xl">
          <p className={`text-[12px] tracking-[0.3em] uppercase mb-6 transition-all duration-700 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E', transitionDelay: '0.1s' }}>
            A RESEARCH PLATFORM &middot; {stats.total_profiles}+ LEADERS
          </p>

          {/* Rotating headline tagline */}
          <div className="mb-10 min-h-[100px] lg:min-h-[130px]">
            <h1 key={`tag-${slideIdx}`} className="text-5xl sm:text-6xl lg:text-[76px] leading-[0.92] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A', animation: 'slideQuoteIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
              {currentSlide.tagline.split(' ').map((word, wi) => {
                const accentWords = ['built', 'changed', 'movement', 'evidence', 'empires', 'authenticated'];
                const isAccent = accentWords.some(a => word.toLowerCase().includes(a));
                return <span key={wi}>{wi > 0 ? ' ' : ''}<span className={isAccent ? 'text-[#C25E3E]' : ''}>{word}</span></span>;
              })}
            </h1>
          </div>

          {/* Rotating quote from real leader */}
          <div className="relative mb-10 min-h-[140px]">
            <div key={slideIdx} style={{ animation: 'slideQuoteIn 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
              <div className="pl-5 border-l-[3px] border-[#C25E3E]">
                <p className="text-lg lg:text-xl italic leading-relaxed mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A' }}>
                  &ldquo;{currentSlide.quote}&rdquo;
                </p>
                <p className="text-[11px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>
                  — {currentSlide.name}
                </p>
                <p className="text-[10px] tracking-[0.15em] uppercase mt-1" style={{ fontFamily: "'Outfit', sans-serif", color: '#888' }}>
                  {currentSlide.role}
                </p>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="flex gap-2 mb-10">
            {HERO_SLIDES.map((s, i) => (
              <button key={i} onClick={() => setSlideIdx(i)} className="group flex flex-col items-start gap-1" data-testid={`slide-dot-${i}`}>
                <div className="relative h-[3px] overflow-hidden transition-all duration-300" style={{ width: i === slideIdx ? '48px' : '24px' }}>
                  {i === slideIdx ? <div className="absolute inset-0 bg-[#C25E3E]" style={{ animation: 'progressFill 5s linear forwards' }} /> : <div className="h-full bg-[#1A1A1A]/15 group-hover:bg-[#C25E3E]/40 transition-colors" />}
                </div>
              </button>
            ))}
          </div>

          <div className={`flex flex-wrap gap-4 transition-all duration-700 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '0.7s' }}>
            <Link to="/profiles" className="group inline-flex items-center h-14 px-10 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.18em] transition-all duration-400 hover:bg-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="cta-read-stories">
              Explore Stories <ArrowRight size={15} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/film" className="group inline-flex items-center h-14 px-10 border-2 border-[#1A1A1A] text-[#1A1A1A] text-[12px] uppercase tracking-[0.18em] transition-all duration-400 hover:bg-[#1A1A1A] hover:text-white" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="cta-watch-film">
              <Play size={15} className="mr-2" fill="currentColor" /> The Film
            </Link>
          </div>
        </div>

        {/* Mobile — show current profile image */}
        <div className="lg:hidden mt-10">
          {currentProfile?.image_url && (
            <div key={slideIdx} className="aspect-[4/3] overflow-hidden" style={{ animation: 'heroImageIn 0.7s ease both' }}>
              <img src={currentProfile.image_url} alt={currentProfile.name} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ========== ROTATING QUOTES ========== */
const ROTATING_QUOTES = [
  { text: 'She spoke. Nobody responded. Four minutes later, a man said almost exactly what she said. The room responded to him.', attr: 'FROM THE BOOK · CHAPTER ONE', highlight: 'The room responded to him.' },
  { text: 'What does it cost a person to build something that will never carry their name?', attr: 'THE CENTRAL QUESTION', highlight: 'carry their name?' },
  { text: 'Fifty years of research. Eight billion dollars a year. Zero programmes proven to produce the outcome they promised.', attr: 'THE ZERO GAP', highlight: 'Zero programmes proven' },
  { text: 'She placed a folder on the table. Every receipt. Every permit. Fifteen years of evidence. Before anyone thought to ask.', attr: 'FROM THE SIZZLE REEL', highlight: 'Before anyone thought to ask.' },
  { text: '676 million women live next to a war. Most of the world\'s builders remain invisible.', attr: 'UN REPORT · 2025', highlight: 'remain invisible.' },
];

const RotatingQuotes = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % ROTATING_QUOTES.length), 4500);
    return () => clearInterval(t);
  }, []);

  const q = ROTATING_QUOTES[idx];
  const parts = q.text.split(q.highlight);

  return (
    <section className="bg-[#FAF9F6] py-24 lg:py-32 relative overflow-hidden" data-testid="quotes-section">
      {/* Animated background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #C25E3E 0%, transparent 70%)', animation: 'pulseGlow 4s ease-in-out infinite' }} />
      </div>

      <div className="max-w-[900px] mx-auto px-6 text-center relative z-10">
        <p className="text-[12px] tracking-[0.3em] uppercase mb-10" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>
          WORDS THAT CHANGED EVERYTHING
        </p>

        {/* Quote with crossfade */}
        <div className="relative min-h-[200px] flex items-center justify-center">
          <div key={idx} style={{ animation: 'quoteRotate 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
            <p className="text-3xl lg:text-[44px] leading-[1.35] italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A' }}>
              &ldquo;{parts[0]}<span className="text-[#C25E3E] not-italic">{q.highlight}</span>{parts[1] || ''}&rdquo;
            </p>
          </div>
        </div>

        <div key={`attr-${idx}`} className="mt-8" style={{ animation: 'fadeInUp 0.6s 0.3s both' }}>
          <p className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Outfit', sans-serif", color: '#888' }}>
            {q.attr}
          </p>
        </div>

        {/* Progress indicators */}
        <div className="flex justify-center gap-3 mt-10">
          {ROTATING_QUOTES.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className="relative w-2 h-2 rounded-full overflow-hidden" data-testid={`quote-dot-${i}`}>
              <div className={`absolute inset-0 rounded-full transition-all duration-300 ${i === idx ? 'bg-[#C25E3E] scale-125' : 'bg-[#1A1A1A]/15 hover:bg-[#C25E3E]/40'}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== CINEMATIC FILM SECTION ========== */
const FILM_IMAGES = [
  'https://images.unsplash.com/photo-1768609956899-2c894c372c25?w=1920&q=80',
  'https://images.unsplash.com/photo-1761005639407-da9aa4caae12?w=1920&q=80',
  'https://images.unsplash.com/photo-1768479619271-e0ff666a14f1?w=1920&q=80',
];

const FILM_LINES = [
  'The stories the world didn\'t tell.',
  'She built empires in silence.',
  'Evidence. Not inspiration.',
];

const CinematicFilm = () => {
  const [filmIdx, setFilmIdx] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setFilmIdx(p => (p + 1) % FILM_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setScrollY(rect.top);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const parallaxOffset = Math.max(-100, Math.min(100, scrollY * 0.15));

  return (
    <section ref={sectionRef} className="relative overflow-hidden" data-testid="film-section" style={{ height: 'clamp(500px, 65vh, 750px)' }}>
      {/* Crossfading background images with parallax */}
      {FILM_IMAGES.map((img, i) => (
        <div key={i} className="absolute inset-0 transition-opacity duration-[1500ms]" style={{ opacity: i === filmIdx ? 1 : 0 }}>
          <img src={img} alt="" className="absolute w-full h-[120%] object-cover" style={{ filter: 'brightness(0.35) saturate(0.8)', top: `${parallaxOffset}px`, transition: 'top 0.1s linear', transform: i === filmIdx ? 'scale(1)' : 'scale(1.08)', transitionProperty: 'transform, opacity', transitionDuration: '8s, 1.5s' }} />
        </div>
      ))}

      {/* Vignette overlay */}
      <div className="absolute inset-0 z-[1]" style={{ boxShadow: 'inset 0 0 200px 60px rgba(0,0,0,0.6)' }} />

      {/* Animated scan line */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
        <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-white/10 to-transparent" style={{ animation: 'scanLine 4s linear infinite' }} />
      </div>

      {/* Film grain */}
      <div className="absolute inset-0 z-[2] opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 512 512\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundRepeat: 'repeat' }} />

      {/* Content */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6">
        <p className="text-[11px] tracking-[0.35em] uppercase mb-6 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, animation: 'fadeInUp 0.8s ease both' }}>
          A FEATURE DOCUMENTARY
        </p>

        <h2 className="text-5xl lg:text-[100px] leading-[0.9] text-white mb-4 tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, textShadow: '0 4px 40px rgba(0,0,0,0.5)' }}>
          She Built This
        </h2>

        {/* Rotating tagline */}
        <div className="relative h-[40px] overflow-hidden mb-10">
          <p key={filmIdx} className="text-lg lg:text-2xl italic text-white/60" style={{ fontFamily: "'Cormorant Garamond', serif", animation: 'slideQuoteIn 0.7s ease both' }}>
            {FILM_LINES[filmIdx]}
          </p>
        </div>

        {/* Dramatic play button with pulse */}
        <Link to="/film" className="group relative" data-testid="film-play-btn">
          <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-white" style={{ animationDuration: '2s' }} />
          <span className="relative w-24 h-24 border-2 border-white/40 flex items-center justify-center transition-all duration-700 group-hover:bg-white/15 group-hover:border-white group-hover:scale-110 group-hover:shadow-[0_0_60px_rgba(255,255,255,0.2)]">
            <Play size={32} className="text-white ml-1.5" fill="white" />
          </span>
        </Link>

        <p className="mt-8 text-[10px] tracking-[0.25em] uppercase text-white/30" style={{ fontFamily: "'Outfit', sans-serif" }}>
          COMING 2026
        </p>
      </div>
    </section>
  );
};

/* ========== MAIN PAGE ========== */
const HomePage = () => {
  const [stats, setStats] = useState({ total_profiles: 370, total_regions: 15 });
  const [allProfiles, setAllProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profilesRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats`),
          axios.get(`${API_URL}/api/stories`),
        ]);
        setStats(statsRes.data);
        const allData = profilesRes.data;
        // Ensure all hero slide profiles are included first
        const heroSlugs = ['huda-kattan', 'randa-sadik', 'falguni-nayar', 'sima-ganwani-ved', 'lisa-su', 'mary-barra'];
        const heroProfs = heroSlugs.map(s => allData.find(p => p.slug === s)).filter(Boolean);
        const withImages = allData.filter(p => p.image_url && p.image_url.trim() !== '' && !heroSlugs.includes(p.slug));
        setAllProfiles([...heroProfs, ...withImages.slice(0, 24)]);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const heroProfiles = allProfiles.slice(0, 18); // 3 sets of 6 for rotation
  const marqueeProfiles = allProfiles.slice(6, 24);

  return (
    <div data-testid="home-page" className="bg-[#FAF9F6]">
      <SEO
        title="Documenting Women Who Built Empires"
        description={`Celebrating ${stats.total_profiles}+ verified women leaders across 7 continents. A book. A film. A movement.`}
        keywords={['women leaders', 'female CEO', 'women entrepreneurs', 'she built this']}
        url="https://shebuiltthis.com"
      />

      {/* ===== DYNAMIC HERO ===== */}
      <DynamicHero profiles={heroProfiles} stats={stats} />

      {/* ===== AUTO-SCROLLING PROFILES MARQUEE ===== */}
      {marqueeProfiles.length > 0 && (
        <section className="bg-[#F3EFE9] py-6 overflow-hidden" data-testid="profiles-marquee">
          <Marquee speed={30} gradient={true} gradientColor="#F3EFE9" gradientWidth={80} pauseOnHover>
            {marqueeProfiles.map((p) => (
              <Link key={p.slug} to={`/profiles/${p.slug}`} className="group flex items-center gap-4 mx-6">
                <img src={p.image_url} alt={p.name} className="w-14 h-14 object-cover rounded-full border-2 border-white shadow-md transition-transform group-hover:scale-110" />
                <div>
                  <p className="text-[13px] font-medium text-[#1A1A1A] group-hover:text-[#C25E3E] transition-colors whitespace-nowrap" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.name}</p>
                  <p className="text-[10px] text-[#888] uppercase tracking-wider whitespace-nowrap" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</p>
                </div>
              </Link>
            ))}
          </Marquee>
        </section>
      )}

      {/* ===== STATS BAR ===== */}
      <section className="bg-[#1A1A1A] py-8" data-testid="stats-bar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12 text-center">
            {[
              { num: stats.total_profiles, suffix: '+', label: 'VERIFIED LEADERS' },
              { num: 15, suffix: '', label: 'REGIONS COVERED' },
              { num: 7, suffix: '', label: 'CONTINENTS' },
              { num: 40, suffix: '', label: 'FREE COURSES' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-4xl lg:text-5xl font-light text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <AnimatedCounter target={s.num} suffix={s.suffix} />
                </p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-white/50" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DYNAMIC ROTATING QUOTES ===== */}
      <RotatingQuotes />

      {/* ===== THE FILM — CINEMATIC ===== */}
      <CinematicFilm />

      {/* ===== THE BOOKS ===== */}
      <section className="bg-[#F3EFE9] py-24 lg:py-32" data-testid="book-section">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[12px] tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>THE BOOK</p>
            <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
              The research that started a movement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Edition 1 — 3D Book */}
            <div className="text-center">
              <Link to="/book" className="group inline-block mb-8">
                <div className="book-3d-wrapper mx-auto" style={{ perspective: '1200px' }}>
                  <div className="relative transition-transform duration-700 group-hover:rotate-y-[-8deg]" style={{ transformStyle: 'preserve-3d', transform: 'rotateY(-5deg)' }}>
                    <img
                      src="https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/rgvc8ddx_SBT_COVER_FRONT.png"
                      alt="She Built This — Edition 1"
                      className="w-[220px] lg:w-[260px] h-auto relative z-10"
                      style={{ filter: 'drop-shadow(8px 12px 30px rgba(0,0,0,0.3))' }}
                    />
                    <div className="absolute top-2 left-[8px] bottom-2 w-[30px] bg-gradient-to-r from-[#8B7355] to-[#A0896C] -z-10" style={{ transform: 'rotateY(90deg) translateZ(-15px) translateX(-15px)', transformOrigin: 'left' }} />
                  </div>
                </div>
              </Link>
              <h3 className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Edition 1</h3>
              <p className="text-sm text-[#4A4A4A] mb-4" style={{ fontFamily: "'Lora', serif" }}>Available now on Amazon Kindle</p>
              <a href="https://a.co/d/0jdiF8K1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-11 px-8 bg-[#C25E3E] text-white text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="home-buy-kindle">
                Buy on Kindle <ArrowRight size={13} className="ml-2" />
              </a>
            </div>

            {/* Edition 2 — 3D Book */}
            <div className="text-center">
              <Link to="/book/edition-2" className="group inline-block mb-8">
                <div className="book-3d-wrapper mx-auto" style={{ perspective: '1200px' }}>
                  <div className="relative transition-transform duration-700 group-hover:rotate-y-[-8deg]" style={{ transformStyle: 'preserve-3d', transform: 'rotateY(-5deg)' }}>
                    <div className="relative w-[220px] lg:w-[260px] aspect-[2/3] bg-gradient-to-br from-[#1A3A2A] to-[#0D2518] flex flex-col items-center justify-center px-6 z-10" style={{ filter: 'drop-shadow(8px 12px 30px rgba(0,0,0,0.3))' }}>
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#D4AF37]" />
                      <p className="text-[9px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#D4AF37' }}>EDITION 2 &middot; 2026</p>
                      <h4 className="text-2xl lg:text-3xl text-center text-white leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>She Built This</h4>
                      <div className="w-10 h-[1px] bg-[#D4AF37] mb-3" />
                      <p className="text-[10px] tracking-wider text-white/50 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>ASIA PACIFIC &middot; MIDDLE EAST</p>
                      <p className="text-[9px] tracking-wider text-[#D4AF37] mt-4" style={{ fontFamily: "'DM Mono', monospace" }}>197 WOMEN LEADERS</p>
                      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#D4AF37]" />
                    </div>
                    <div className="absolute top-2 left-[8px] bottom-2 w-[30px] bg-gradient-to-r from-[#0D2518] to-[#1A3A2A] -z-10" style={{ transform: 'rotateY(90deg) translateZ(-15px) translateX(-15px)', transformOrigin: 'left' }} />
                  </div>
                </div>
              </Link>
              <h3 className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Edition 2</h3>
              <p className="text-sm text-[#C25E3E] mb-4" style={{ fontFamily: "'Lora', serif" }}>3 Preview Chapters Available</p>
              <Link to="/book/edition-2" className="inline-flex items-center h-11 px-8 border-2 border-[#1A1A1A] text-[#1A1A1A] text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] hover:text-white transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                Read Preview <ArrowRight size={13} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PILLARS — DYNAMIC ===== */}
      <section className="bg-[#FAF9F6] py-24 lg:py-32" data-testid="platform-pillars">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[12px] tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>THE PLATFORM</p>
            <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>More than a film. More than a book.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Stories', stat: `${stats.total_profiles}+`, statLabel: 'Verified Profiles', desc: 'Every story authenticated. Every photograph verified. Across 7 continents.', link: '/profiles', img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80', num: '01' },
              { title: 'Courses', stat: '40', statLabel: 'Free Programmes', desc: 'Executive-grade courses with certificates. From leadership to digital transformation.', link: '/youth', img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80', num: '02' },
              { title: 'Podcast', stat: '3', statLabel: 'Hosts, One Question', desc: 'Three hosts. One guest. The question that drives everything.', link: '/podcast', img: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=600&q=80', num: '03' },
              { title: 'Participate', stat: '2026', statLabel: 'UAE Launch', desc: 'Speak. Mentor. Attend. The first live event. Coming to the UAE.', link: '/participate', img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80', num: '04' },
            ].map((p, i) => (
              <Link key={p.title} to={p.link} className="group relative overflow-hidden bg-white border border-[#1A1A1A]/5 transition-all duration-500 hover:shadow-xl hover:-translate-y-1" data-testid={`pillar-${p.title.toLowerCase()}`}>
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" style={{ filter: 'brightness(0.85) saturate(0.9)' }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-[#1A1A1A]/20 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="text-[10px] tracking-[0.2em] text-white/60" style={{ fontFamily: "'DM Mono', monospace" }}>{p.num}</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-3xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.stat}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.statLabel}</p>
                  </div>
                </div>
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl mb-2 group-hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>{p.title}</h3>
                  <p className="text-[13px] leading-relaxed mb-4" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>{p.desc}</p>
                  <span className="inline-flex items-center text-[11px] tracking-[0.12em] uppercase text-[#C25E3E] group-hover:gap-2 transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                    Explore <ArrowRight size={12} className="ml-1 transition-transform group-hover:translate-x-2" />
                  </span>
                </div>
                {/* Bottom accent */}
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#C25E3E] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUOTE ===== */}
      <section className="bg-[#1A1A1A] py-24 lg:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="text-3xl lg:text-[44px] italic leading-[1.4] text-white" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            &ldquo;She spoke. Nobody responded. Four minutes later, a man said almost exactly what she said. <span className="text-[#C25E3E]">The room responded to him.</span>&rdquo;
          </p>
          <p className="text-[10px] tracking-[0.3em] uppercase mt-10 text-white/40" style={{ fontFamily: "'Outfit', sans-serif" }}>FROM THE BOOK &middot; CHAPTER ONE</p>
        </div>
      </section>

      {/* ===== NOMINATE ===== */}
      <section className="bg-[#FAF9F6] py-24 lg:py-32" data-testid="nominate-section">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="w-16 h-[2px] mx-auto mb-10 bg-[#C25E3E]" />
          <h2 className="text-4xl lg:text-[52px] leading-[1.1] tracking-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Know a woman who belongs here?</h2>
          <p className="text-base mb-10" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>Every nomination is reviewed personally by Dr. Neha Dewan.</p>
          <Link to="/nominate" className="inline-flex items-center h-14 px-10 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.18em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="cta-nominate">
            Nominate a Woman <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>

      {/* AI FAB */}
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} className="fixed bottom-6 right-6 z-[70] w-14 h-14 bg-[#C25E3E] text-white shadow-lg flex items-center justify-center hover:bg-[#1A1A1A] transition-all duration-300 hover:scale-110" data-testid="ai-chat-fab">
          <MessageCircle size={22} />
        </button>
      )}
      {chatOpen && <AIChatBot onClose={() => setChatOpen(false)} />}
    </div>
  );
};

export default HomePage;
